import { getLiteral } from 'utils/getLiteral';

/** API marketing campaign workflow status id (integer). */
export const MARKETING_CAMPAIGN_STATUS = {
    DRAFT: 0,
    SCHEDULED: 1,
    SENDING: 2,
    SENT: 3,
    CANCELED: 4,
    ERROR: 5,
    LIMIT_REACHED: 6,
};

/** Literal keys (CMS). Error follows the same naming pattern — add when available. */
export const MARKETING_CAMPAIGN_STATUS_LITERAL_KEYS = {
    [MARKETING_CAMPAIGN_STATUS.DRAFT]: 'label_marketing_campaigns_status_draft',
    [MARKETING_CAMPAIGN_STATUS.SCHEDULED]: 'label_marketing_campaigns_status_scheduled',
    [MARKETING_CAMPAIGN_STATUS.SENDING]: 'label_marketing_campaigns_status_sending',
    [MARKETING_CAMPAIGN_STATUS.SENT]: 'label_marketing_campaigns_status_sent',
    [MARKETING_CAMPAIGN_STATUS.CANCELED]: 'label_marketing_campaigns_status_canceled',
    [MARKETING_CAMPAIGN_STATUS.ERROR]: 'label_marketing_campaigns_status_error',
    [MARKETING_CAMPAIGN_STATUS.LIMIT_REACHED]: 'label_marketing_campaigns_status_limit_reached',
};

/** Options for entity list filters (`value` as string, matching other singleValueList filters). */
export function getMarketingCampaignStatusFilterOptions() {
    return Object.values(MARKETING_CAMPAIGN_STATUS).map((statusId) => ({
        label: getLiteral(MARKETING_CAMPAIGN_STATUS_LITERAL_KEYS[statusId]),
        value: String(statusId),
    }));
}

/**
 * Coerces API value to an integer status id; missing/invalid → Draft (0).
 * JSON may send numeric strings; we still normalize to number.
 *
 * @param {number|string|null|undefined} value
 * @returns {number}
 */
export function normalizeMarketingCampaignStatusId(value) {
    if (value === null || value === undefined || value === '') {
        return MARKETING_CAMPAIGN_STATUS.DRAFT;
    }
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n) || Number.isNaN(n)) {
        return MARKETING_CAMPAIGN_STATUS.DRAFT;
    }
    return Math.trunc(n);
}

/**
 * Row click opens the editor (not read-only details) for these statuses.
 *
 * @param {number|string|null|undefined} statusId
 */
export function shouldOpenMarketingCampaignEditor(statusId) {
    const id = normalizeMarketingCampaignStatusId(statusId);

    return [
        MARKETING_CAMPAIGN_STATUS.DRAFT,
        MARKETING_CAMPAIGN_STATUS.SCHEDULED,
        MARKETING_CAMPAIGN_STATUS.LIMIT_REACHED,
    ].includes(id);
}

/**
 * @param {number|string|null|undefined} statusId
 */
export function getMarketingCampaignStatusLabel(statusId) {
    const id = normalizeMarketingCampaignStatusId(statusId);
    const key = MARKETING_CAMPAIGN_STATUS_LITERAL_KEYS[id];
    if (key) {
        return getLiteral(key);
    }
    return String(id);
}

/**
 * Badge `type` for @Sage/hoi-poi-ui from status id.
 *
 * @param {number|string|null|undefined} statusId
 */
export function getMarketingCampaignStatusBadgeType(statusId) {
    const id = normalizeMarketingCampaignStatusId(statusId);
    switch (id) {
        case MARKETING_CAMPAIGN_STATUS.DRAFT:
            return 'outlined';
        case MARKETING_CAMPAIGN_STATUS.SCHEDULED:
            return 'default';
        case MARKETING_CAMPAIGN_STATUS.SENDING:
            return 'actionMinor';
        case MARKETING_CAMPAIGN_STATUS.SENT:
            return 'semanticPositive';
        case MARKETING_CAMPAIGN_STATUS.CANCELED:
            return 'caution';
        case MARKETING_CAMPAIGN_STATUS.ERROR:
            return 'semanticNegative';
        case MARKETING_CAMPAIGN_STATUS.LIMIT_REACHED:
            return 'semanticNegative';
        default:
            return 'default';
    }
}
